import React, { useMemo } from 'react';
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import TanStackTable from "../../../components/TanStackTable";
import Chip from "../../../components/Chip";
import { FaEye } from "react-icons/fa";
import { useGetSupportTickets } from '../../../queries/partner';
import { renderPriorityColor } from '../../../utils/chipColors/priorityColor';
import { renderActiveResolvedColor } from '../../../utils/chipColors/activeResolved';

const SupportTicketList = () => {
    const navigate = useNavigate();
    const columnHelper = createColumnHelper();

    const { data, isPending } = useGetSupportTickets();
    
    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('campaign', {
            header: 'Campaign',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue()?.title || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('issueType', {
            header: 'Issue Type',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor(row => row.NGOId || row.companyId, {
            id: 'sponsor',
            header: 'Sponsor',
            cell: info => {
                const ngoId = info.row.original.ngoId;
                const companyId = info.row.original.companyId;
                return (
                    <div className="whitespace-normal break-words line-clamp-2">
                        {ngoId?.ngoName ? `NGO: ${ngoId?.ngoName}` : companyId?.companyName ? `Company: ${companyId?.companyName}` : 'N/A'}
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'createdAt',
            align: 'center',
            header: 'Created At',
            cell: info => {
                return (
                    <div className="whitespace-normal break-words">
                        {info.row.original.createdAt.toRegularDate()}
                    </div>
                );
            },
        }),
        columnHelper.accessor('priority', {
            header: () => <div className="text-center">Priority</div>,
            align: 'center',
            cell: info => (
                <Chip
                    value={info.getValue() || '-'}
                    color={renderPriorityColor(info.getValue())}
                    disabled={!info.row.original.active}
                />
            ),
        }),
        columnHelper.accessor('active', {
            header: () => <div className="text-center">Status</div>,
            align: 'center',
            cell: info => (
                <Chip
                    value={info.getValue() ? 'Active' : 'Resolved'}
                    color={renderActiveResolvedColor(info.getValue())}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: () => <div className="text-center">Actions</div>,
            enableColumnFilter: false,
            align: 'center',
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/requests/view-ticket', { state: { requestId: info.row.original?._id } })}
                            title="View Request"
                        >
                            <FaEye className="fill-primary" />
                        </button>
                    </div>
                )
            },
        }),
    ], [navigate]);

    if(isPending) {
        return (
            <section className="flex flex-col py-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className='text-base text-center'>Loading...</div>
            </section>
        );
    }
    
    return (
        <section className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
            <div className="flex flex-col pr-px w-full max-md:max-w-full">
                <h1 className="self-start text-lg font-bold pl-6 pb-4">List of Partner Support Tickets</h1>
                <div className="pl-5">
                    <TanStackTable
                        data={data || []}
                        columns={columns}
                        enablePagination={true}
                    />
                </div>
            </div>
        </section>
    );
}

export default SupportTicketList;